import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WorkshopLocationManager.css';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const emptyLocation = {
  name: '',
  slug: '',
  address_line1: '',
  city: '',
  state: '',
  zip_code: '',
  timezone: 'America/New_York',
  max_capacity: 6,
  is_active: true,
  operating_hours: {},
  blocked_dates: []
};

function WorkshopLocationManager() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({ ...emptyLocation });
  const [saving, setSaving] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState('');

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/workshops/locations');
      setLocations(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location.id);
    setFormData({
      ...location,
      operating_hours: location.operating_hours || {},
      blocked_dates: location.blocked_dates || []
    });
  };

  const handleNew = () => {
    setEditingLocation('new');
    setFormData({ ...emptyLocation });
  };

  const handleCancel = () => {
    setEditingLocation(null);
    setFormData({ ...emptyLocation });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHoursChange = (dayIndex, field, value) => {
    setFormData((prev) => {
      const hours = { ...prev.operating_hours };
      if (!hours[dayIndex]) hours[dayIndex] = { open: '', close: '', closed: false };
      hours[dayIndex] = { ...hours[dayIndex], [field]: value };
      return { ...prev, operating_hours: hours };
    });
  };

  const handleAddBlockedDate = () => {
    if (!newBlockedDate) return;
    setFormData((prev) => ({
      ...prev,
      blocked_dates: [...(prev.blocked_dates || []), newBlockedDate]
    }));
    setNewBlockedDate('');
  };

  const handleRemoveBlockedDate = (dateToRemove) => {
    setFormData((prev) => ({
      ...prev,
      blocked_dates: (prev.blocked_dates || []).filter((d) => d !== dateToRemove)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editingLocation === 'new') {
        await axios.post('/api/workshops/locations', formData);
      } else {
        await axios.put(`/api/workshops/locations/${editingLocation}`, formData);
      }
      setEditingLocation(null);
      setFormData({ ...emptyLocation });
      await fetchLocations();
    } catch (err) {
      console.error('Failed to save location:', err);
      alert('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="workshop-location-manager">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workshop-location-manager">
      <div className="ws-page-header">
        <h1>Workshop Locations</h1>
        <button className="ws-btn-primary" onClick={handleNew}>
          Add Location
        </button>
      </div>

      {error && <div className="ws-error">{error}</div>}

      {editingLocation && (
        <div className="ws-section ws-edit-form">
          <h2>{editingLocation === 'new' ? 'New Location' : 'Edit Location'}</h2>

          <div className="ws-form-grid">
            <div className="ws-form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Charleston Studio"
              />
            </div>
            <div className="ws-form-group">
              <label>Slug</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="e.g. charleston"
              />
            </div>
            <div className="ws-form-group">
              <label>Address</label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={(e) => handleChange('address_line1', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
                maxLength={2}
              />
            </div>
            <div className="ws-form-group">
              <label>Zip Code</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => handleChange('zip_code', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>Timezone</label>
              <select
                value={formData.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
              >
                <option value="America/New_York">Eastern</option>
                <option value="America/Chicago">Central</option>
                <option value="America/Denver">Mountain</option>
                <option value="America/Los_Angeles">Pacific</option>
              </select>
            </div>
            <div className="ws-form-group">
              <label>Max Capacity</label>
              <input
                type="number"
                value={formData.max_capacity}
                onChange={(e) => handleChange('max_capacity', parseInt(e.target.value) || 0)}
                min="1"
              />
            </div>
            <div className="ws-form-group">
              <label>Active</label>
              <select
                value={formData.is_active ? 'true' : 'false'}
                onChange={(e) => handleChange('is_active', e.target.value === 'true')}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <h3>Operating Hours</h3>
          <div className="ws-hours-grid">
            {DAYS.map((day, index) => {
              const dayHours = formData.operating_hours[index] || {};
              return (
                <div key={day} className="ws-hours-row">
                  <span className="ws-hours-day">{day}</span>
                  <label className="ws-hours-closed">
                    <input
                      type="checkbox"
                      checked={dayHours.closed || false}
                      onChange={(e) => handleHoursChange(index, 'closed', e.target.checked)}
                    />
                    Closed
                  </label>
                  {!dayHours.closed && (
                    <>
                      <input
                        type="time"
                        value={dayHours.open || ''}
                        onChange={(e) => handleHoursChange(index, 'open', e.target.value)}
                        className="ws-hours-input"
                      />
                      <span className="ws-hours-separator">to</span>
                      <input
                        type="time"
                        value={dayHours.close || ''}
                        onChange={(e) => handleHoursChange(index, 'close', e.target.value)}
                        className="ws-hours-input"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <h3>Blocked Dates</h3>
          <div className="ws-blocked-dates">
            <div className="ws-blocked-date-add">
              <input
                type="date"
                value={newBlockedDate}
                onChange={(e) => setNewBlockedDate(e.target.value)}
              />
              <button className="ws-btn-secondary ws-btn-sm" onClick={handleAddBlockedDate}>
                Add
              </button>
            </div>
            {(formData.blocked_dates || []).length === 0 ? (
              <div className="ws-empty-sm">No blocked dates</div>
            ) : (
              <div className="ws-blocked-date-list">
                {formData.blocked_dates.map((date) => (
                  <div key={date} className="ws-blocked-date-item">
                    <span>{date}</span>
                    <button
                      className="ws-remove-btn"
                      onClick={() => handleRemoveBlockedDate(date)}
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ws-form-actions">
            <button className="ws-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Location'}
            </button>
            <button className="ws-btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="ws-section">
        <h2>Locations</h2>
        {locations.length === 0 ? (
          <div className="ws-empty">No locations configured</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <td className="ws-text-bold">{location.name}</td>
                    <td>{location.city}</td>
                    <td>{location.state}</td>
                    <td>{location.max_capacity}</td>
                    <td>
                      <span className={`ws-badge ${location.is_active ? 'ws-badge-confirmed' : 'ws-badge-cancelled'}`}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button className="ws-btn-secondary ws-btn-sm" onClick={() => handleEdit(location)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopLocationManager;
